import { useParams, Link, Navigate } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { features, getFeatureBySlug, STATUS_META } from '@/content/features';
import { FeatureIllustration } from '@/components/illustrations/FeatureIllustration';
import { IllustrationCaption } from '@/components/illustrations/IllustrationFrame';
import { NotifyForm } from '@/components/NotifyForm';
import { useSeo } from '@/seo/useSeo';

export function FeatureDetail(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const feature = getFeatureBySlug(slug);

  useSeo({
    title: feature ? feature.title : 'Feature not found',
    description: feature ? feature.body : 'This feature page could not be found.',
    path: `/platform/${slug ?? ''}`,
  });

  if (!feature) return <Navigate to="/platform" replace />;

  const Icon = feature.icon;
  const related = features.filter((f) => f.slug !== feature.slug).slice(0, 3);

  return (
    <div>
      <section className="relative overflow-hidden pt-16 pb-16 md:pt-24 md:pb-24">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-nexus-grid bg-grid [mask-image:radial-gradient(circle_at_50%_0%,#000,transparent_70%)]"
        />
        <div className="container-content relative">
          <p className="text-xs text-white/40">
            <Link to="/platform" className="text-white/40 no-underline hover:text-nexus-purple-light">
              Platform
            </Link>{' '}
            / {feature.title}
          </p>

          <div className="mt-6 grid grid-cols-1 items-center gap-12 md:grid-cols-[1.05fr_0.95fr] md:gap-14">
            <div>
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                <Icon fontSize="medium" />
              </div>
              <div className="mb-4 flex flex-wrap gap-2">
                {feature.badge && (
                  <Chip
                    label={feature.badge}
                    size="small"
                    className="h-[22px] border border-nexus-purple/35 bg-transparent text-[10.5px] font-bold tracking-wide text-nexus-purple-light"
                  />
                )}
                <Chip
                  label={STATUS_META[feature.status].detail}
                  size="small"
                  className="h-[22px] border border-white/15 bg-transparent text-[10.5px] font-semibold text-white/55"
                />
              </div>
              <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-nexus-ink md:text-[44px]">
                {feature.title}
              </h1>
              <p className="mt-5 text-lg text-white/70">{feature.intro}</p>
            </div>

            <div>
              <FeatureIllustration kind={feature.illustration} />
              <IllustrationCaption />
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">What it does</h2>
          <ul className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            {feature.capabilities.map((c) => (
              <li key={c} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <CheckCircleOutlineIcon fontSize="small" className="mt-0.5 flex-shrink-0 text-nexus-purple-light" />
                <span className="text-sm text-white/75">{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <h2 className="font-display text-2xl font-bold text-nexus-ink md:text-3xl">Other modules</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {related.map((f) => {
              const RelIcon = f.icon;
              return (
                <Link
                  key={f.slug}
                  to={`/platform/${f.slug}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                    <RelIcon fontSize="small" />
                  </div>
                  <h3 className="font-display text-[15px] font-semibold text-nexus-ink">{f.title}</h3>
                  <p className="mt-1.5 text-sm text-white/65">{f.body}</p>
                </Link>
              );
            })}
          </div>
          <div className="mt-8 flex justify-center">
            <Button component={Link} to="/platform" variant="outlined" endIcon={<ArrowForwardIcon />} className="border-white/15 normal-case">
              View all modules
            </Button>
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
            <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
              {feature.status === 'live' || feature.status === 'configure'
                ? `Run ${feature.title} yourself.`
                : `Be first in line for ${feature.title}.`}
            </h2>
            <p className="mx-auto mt-3 max-w-[520px] text-white/70">
              {feature.status === 'live'
                ? `This module is live in our own MSP practice today. Join the design-partner cohort to run it in yours.`
                : feature.status === 'configure'
                ? `This module is built and wired — it runs on sample data until you connect your own integration. Join the design-partner cohort for early access.`
                : `Nexus is actively building this module in the open. Join the private-preview list and we'll reach out as it graduates from building to design-partner ready.`}
            </p>
            <div className="mt-8 flex justify-center">
              <NotifyForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
